import * as React from 'react';

import { cn } from '@/ux-common/lib/utils';

/**
 * Card component - Minimal React wrapper for behavioral concerns.
 *
 * A single themed presentational surface (`div`) that treats its children as
 * content. There are NO sub-components — compose layout (Stack/Gutter) and
 * typography (Typography/Header) inside. All visual styling is applied via CSS
 * classes. This wrapper provides:
 * - `data-component="Card"` for component identification
 * - Ref forwarding for HOC/tooltip compatibility
 * - `className` merging via `cn()` (caller utilities compose on top)
 *
 * The `.card` base class is applied automatically — `className` composes
 * modifiers and utilities on top of it.
 *
 * @example
 * ```tsx
 * // Default themed surface (gets .card; --card-padding, --card-radius-default)
 * <Card>
 *   <Stack>…content…</Stack>
 * </Card>
 *
 * // className override wins (utilities beat @layer components)
 * <Card className="p-0 overflow-hidden">…edge-to-edge media/chart…</Card>
 *
 * // Larger corners (--card-radius-lg)
 * <Card className="card-radius-lg">…</Card>
 *
 * // Clickable: hover lightens, press shows white border
 * <Card className="card-interactive" role="button" tabIndex={0} onClick={onSelect}>
 *   …
 * </Card>
 *
 * // Selected/current: persistent white border
 * <Card className={cn('card-interactive', isSelected && 'card-selected')}>…</Card>
 * ```
 */

export type CardProps = React.HTMLAttributes<HTMLDivElement>;

export const Card = React.forwardRef<HTMLDivElement, CardProps>(({ className, ...props }, ref) => {
  return <div ref={ref} data-component="Card" className={cn('card', className)} {...props} />;
});

Card.displayName = 'Card';
